import React from "react";
import { LogOut, X, AlertTriangle } from "lucide-react";
import { useUiLang } from "../../../context/UiLangContext";

interface LogoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  recording: boolean;
}

export function LogoutConfirmModal({ isOpen, onClose, onConfirm, recording }: LogoutConfirmModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const [isPending, setIsPending] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsPending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    
    setIsPending(true);
    onConfirm();
    onClose();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className={`bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-sm w-full shadow-2xl overflow-hidden animate-[modal-appear_0.18s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)] transition-opacity duration-200 ${
          isPending ? "pointer-events-none opacity-80" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4.5 py-3.5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <LogOut size={16} className="text-rose-400" />
            <h3 className="text-sm font-bold tracking-tight text-[var(--text)]">
              {isEn ? "Log Out" : "Cerrar sesión"}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="w-6 h-6 rounded-full bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={13} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4.5 py-4.5 flex flex-col gap-3">
          <p className="text-[13px] text-[var(--text-muted)] leading-[1.6]">
            {isEn
              ? "Are you sure you want to log out? Any unsaved session history will be lost."
              : "¿Estás seguro que querés cerrar sesión? Si tenés una sesión en curso sin guardar, se perderá el historial."}
          </p>

          {recording && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[12px] leading-[1.5]">
              <AlertTriangle size={15} className="text-rose-400 flex-shrink-0 mt-0.5" />
              <span>
                {isEn
                  ? "Warning: You currently have an active recording in progress. Logging out will stop the recording and discard unsaved audio."
                  : "Advertencia: Actualmente tenés una grabación activa en curso. Si cerrás sesión ahora, se detendrá la grabación y se perderá el historial no guardado."}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4.5 py-3 border-t border-[var(--border)]">
          <button
            type="button"
            onClick={handleClose}
            className="px-3 py-1.5 rounded-lg border border-[var(--border)] bg-transparent hover:bg-[var(--surface-hover)] text-[11.5px] font-semibold transition-all text-[var(--text)]"
          >
            {isEn ? "Cancel" : "Cancelar"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-3 py-1.5 rounded-lg border border-rose-500/30 bg-rose-950/35 hover:bg-rose-900/50 text-rose-300 text-[11.5px] font-semibold transition-all flex items-center gap-1.5 shadow-sm active:scale-[0.98]"
          >
            <LogOut size={13} />
            <span>{isEn ? "Yes, Log Out" : "Sí, cerrar sesión"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
